/**
 * useSessionTypes (React Query 단순캐시, 옵션2 ISSUE_167) — 팩토리 듀얼-워크스페이스 첫 적용
 *
 * 중캐시(학원 설정). **academy + scratch(개인) 둘 다** 지원 — createCachedListDomain 의
 * useWorkspaceResolver 로 academy_id|owner_id 분기. scratch 는 sync OFF → realtime 미구독.
 *
 * 주의: 기존 직접-fetch 훅 `core/hooks/master-data/useSessionTypes.ts`(Dexie 무의존)는 별개로 유지.
 *
 * @module core/query/useSessionTypes
 */

import { queryKeys } from "~/shared/lib/query-client";
import { useSessionTypesStore } from "~/core/store/useSessionTypesStore";
import { useWorkspaceContext } from "~/core/query/useStudents";
import {
  createCachedListDomain,
  type DomainWorkspace,
} from "~/core/query/createCachedListDomain";
import {
  fetchActiveSessionTypes,
  createSessionTypeRemote,
  updateSessionTypeRemote,
  deleteSessionTypeRemote,
  reorderSessionTypesRemote,
  type SessionType,
  type CreateSessionTypeInput,
  type UpdateSessionTypeInput,
} from "~/services/sessions/sessionTypesService";

const SESSION_TYPES_STALE_TIME = 15 * 60 * 1000; // 15분 (중캐시)

type SessionTypeFetchArg = { academyId?: string | null; ownerId?: string | null };

export function sessionTypesListKey(workspaceKey: string) {
  return queryKeys.sessionTypes.list(workspaceKey);
}

/** CreateSessionTypeInput + id → 낙관 SessionType row */
function buildSessionType(input: CreateSessionTypeInput, id: string): SessionType {
  const now = new Date().toISOString();
  const t: SessionType = {
    id,
    academyId: input.academyId ?? null,
    ownerId: input.ownerId ?? null,
    name: input.name,
    code: input.code,
    color: input.color,
    capacityLimit: input.capacityLimit ?? null,
    capacityPriority: input.capacityPriority ?? 50,
    sortOrder: input.sortOrder ?? 0,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  };
  if (input.description) t.description = input.description;
  return t;
}

/** academy 워크스페이스 resolver: academy_id 필터, realtime 구독 */
function useSessionTypeWorkspace(): DomainWorkspace<SessionTypeFetchArg> | null {
  const ws = useWorkspaceContext();
  if (!ws) return null;
  return {
    key: ws.academyId!,
    fetchArg: { academyId: ws.academyId! },
    realtimeValue: ws.academyId!,
    realtimeColumn: "academy_id",
  };
}

const domain = createCachedListDomain<
  SessionType,
  CreateSessionTypeInput,
  UpdateSessionTypeInput,
  SessionTypeFetchArg
>({
  queryKey: (key) => queryKeys.sessionTypes.list(key),
  fetchFn: fetchActiveSessionTypes,
  remote: {
    create: createSessionTypeRemote,
    update: updateSessionTypeRemote,
    delete: deleteSessionTypeRemote,
    reorder: reorderSessionTypesRemote,
  },
  buildOptimistic: buildSessionType,
  useStoreSetter: () => useSessionTypesStore((s) => s.setTypes),
  realtimeTable: "session_types",
  staleTime: SESSION_TYPES_STALE_TIME,
  useWorkspaceResolver: useSessionTypeWorkspace,
});

export const useSessionTypesQuery = domain.useList;

/** 미러 투영(→ useSessionTypesStore.setTypes) + realtime(academy만). sessionTypes 쓰는 곳에서 마운트. */
export function useSessionTypesMirror(): { types: SessionType[]; isLoading: boolean } {
  const { items, isLoading } = domain.useMirror();
  return { types: items, isLoading };
}

export const useCreateSessionTypeMutation = domain.useCreate;
export const useUpdateSessionTypeMutation = domain.useUpdate;
export const useDeleteSessionTypeMutation = domain.useDelete;
export const useReorderSessionTypesMutation = domain.useReorder;

/** 워크스페이스 sessionTypes 캐시 무효화 (비-React) */
export const invalidateSessionTypes = domain.invalidate;
